import type { GanttData, ProjectMeta } from '../types';
import { dateRange, parseIso, toIso } from './dates';

/** Escape one TSV cell: quote if contains tab/newline/quote, double internal quotes. */
export function escapeTsv(value: string): string {
  if (value == null) return '';
  if (/[\t\n"]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Merge all groups' items for a date into one cell value (deduped, comma-joined). */
export function cellForDate(data: GanttData, groups: string[], iso: string): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const g of groups) {
    for (const item of data[g]?.[iso] ?? []) {
      if (!seen.has(item)) { seen.add(item); out.push(item); }
    }
  }
  return out.join(', ');
}

/** Format ISO YYYY-MM-DD → YYYY/M/D for sheet metadata cells */
export function isoToSlash(iso: string): string {
  const d = parseIso(iso);
  if (!d) return '';
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

/**
 * Format A: 整列 TSV 給「2026」分頁從 A 欄開始貼。
 * 12 個 metadata 欄 + 從 1/1 起 N 個日期欄。
 *
 * 欄位順序：
 *   A 目前狀態, B 案號, C 地址, D 設計, E 工務,
 *   F 合約金額, G 開工日, H 預計完工日, I 協調延宕天數, J 剩餘工期,
 *   K 實際進度（留空，由 Sheet 公式計算）, L 目前進度,
 *   M+ 1/1, 1/2, ..., 12/31
 */
export function tsvFullRow(
  meta: ProjectMeta,
  data: GanttData,
  groups: string[],
  year: number
): string {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const dates = dateRange(yearStart, yearEnd);
  const fields = [
    meta.status,
    meta.caseNo,
    meta.address,
    meta.designer,
    meta.foreman,
    meta.contractAmount,
    isoToSlash(meta.startDate),
    isoToSlash(meta.endDate),
    meta.delayDays,
    '', // K 實際進度（由 Sheet 公式填）
    meta.notes,
    '', // L 目前進度（保留給 Sheet 公式）
    ...dates.map(d => cellForDate(data, groups, d)),
  ];
  return fields.map(escapeTsv).join('\t');
}

/**
 * Format B: 只有日期區段 TSV，從 startDate 到 endDate（含），用於補貼某段工期。
 * 一行、N 格，每格 = 該日所有 group 的工項合併。
 */
export function tsvDateRange(
  data: GanttData,
  groups: string[],
  startIso: string,
  endIso: string
): string {
  const dates = dateRange(startIso, endIso);
  return dates.map(d => escapeTsv(cellForDate(data, groups, d))).join('\t');
}

/** Helper: detect dominant year from project dates, fallback to current year */
export function detectYear(meta: ProjectMeta): number {
  const candidates = [meta.startDate, meta.endDate]
    .map(s => parseIso(s)?.getFullYear())
    .filter((y): y is number => typeof y === 'number');
  if (candidates.length > 0) return candidates[0];
  return new Date().getFullYear();
}

export function emptyToIso(_: string): string {
  return toIso(new Date());
}
